import * as React from 'react';
import { useState } from 'react';
import { Title } from '@gje/typography/lib';
import { PrimaryButton, SecondaryButton } from '@gje/buttons';
import { getText } from '../../../services/text/text-service';
import PlusPackageCoverage from './PlusPackageCoverage';
import { resolvePaymentTerm } from '../../../services/calculation-data/calculation-data-service';
import { Coverage } from '../../../types/Coverage';
import useMobile from '../../../hooks/useMobile';
import { numberWithSpaces } from '../../../utils/stringUtils';
import classNames from 'classnames';
import { isVillaagarna } from '../../../services/partners/partnersService';
import { isIPad13, isTablet } from 'react-device-detect';

interface PlusPackageProps {
    handlePlusPackageBuy: Function;
    price: number;
    paymentTerm: number;
    coverages: Coverage[];
    selectedInsuranceName: string;
    plusPackageBought: boolean;
}

const PlusPackage: React.FC<PlusPackageProps> = ({
    handlePlusPackageBuy,
    price,
    paymentTerm,
    selectedInsuranceName,
    plusPackageBought,
    coverages
}) => {
    const [openCoverageIndex, setOpenCoverageIndex] = useState(-1);
    const isMobile = useMobile();

    const renderPlusPackageInfo = (coverages: Coverage[]) => {
        return coverages.map((coverage, index) => {
            return (
                <PlusPackageCoverage
                    key={index}
                    plusPackageCoverageData={coverage}
                    isOpen={openCoverageIndex === index}
                    onClick={() =>
                        setOpenCoverageIndex(
                            index === openCoverageIndex ? -1 : index
                        )
                    }
                />
            );
        });
    };

    const handleClickAnywhere = e => {
        if (
            !e.target.classList.contains('dont-hide-card-click') &&
            e.target.tagName != 'H6'
        ) {
            setOpenCoverageIndex(-1);
        }
    };

    return (
        <>
            {isMobile && !(isTablet || isIPad13) ? (
                <div
                    className={classNames(
                        'row',
                        `plus-package${isVillaagarna() ? '-villaagarna' : ''}`,
                        'mobile-padding'
                    )}
                    onClick={handleClickAnywhere}>
                    <div className="row">
                        <div className="col-xs-23 align-left">
                            <Title tag="h3">
                                {getText('webshopsweden.common.plusPackage')}
                            </Title>
                            <Title
                                tag="h4"
                                className="margin-top-13 title-without-bold">
                                {getText(
                                    'webshopsweden.common.plusPackageDescription'
                                ).replace(
                                    '${product}',
                                    selectedInsuranceName.toLowerCase()
                                )}
                            </Title>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-xs-16">
                            <ul
                                key="plusPackage"
                                className="margin-top-13 plus-package-coverages-list">
                                {renderPlusPackageInfo(coverages)}
                            </ul>
                        </div>
                        <div className="col-xs-7 margin-top-13 no-padding">
                            <div className="row">
                                <div className="col-xs-20 col-xs-offset-4">
                                    <Title
                                        tag="h2"
                                        className="text-center margin-bottom-13 mobile-line-height">
                                        {numberWithSpaces(price)}
                                        <Title
                                            tag={'h3'}
                                            className={'title-without-bold'}>
                                            {resolvePaymentTerm(
                                                paymentTerm,
                                                price
                                            )}
                                        </Title>
                                    </Title>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-xs-20 col-xs-offset-4 no-padding">
                                    {plusPackageBought ? (
                                        <SecondaryButton
                                            onClick={e =>
                                                handlePlusPackageBuy(e)
                                            }
                                            className="mobile-button-padding w-100">
                                            <Title
                                                tag={'h5'}
                                                children={getText(
                                                    'webshopsweden.common.buyingInformation.remove'
                                                )}
                                            />
                                        </SecondaryButton>
                                    ) : (
                                        <PrimaryButton
                                            borderColor={'red'}
                                            flexible={true}
                                            onClick={e =>
                                                handlePlusPackageBuy(e)
                                            }
                                            className={classNames(
                                                'mobile-button-padding',
                                                isVillaagarna()
                                                    ? 'villaagarna-green-bg-color no-border villaagarna-button'
                                                    : ''
                                            )}>
                                            <Title
                                                tag={'h4'}
                                                children={getText(
                                                    'webshopsweden.common.cardButton'
                                                )}
                                            />
                                        </PrimaryButton>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div
                    className={classNames(
                        'row',
                        `plus-package${isVillaagarna() ? '-villaagarna' : ''}`
                    )}
                    onClick={handleClickAnywhere}>
                    <div className="col-sm-1-5" />
                    <div className="col-sm-14 align-left">
                        <Title tag="h3">
                            {getText('webshopsweden.common.plusPackage')}
                        </Title>
                        <Title
                            tag="h4"
                            className="margin-top-13 title-without-bold">
                            {getText(
                                'webshopsweden.common.plusPackageDescription'
                            ).replace(
                                '${product}',
                                selectedInsuranceName.toLowerCase()
                            )}
                        </Title>
                        <ul
                            key="plusPackage"
                            className="margin-top-13 plus-package-coverages-list">
                            {renderPlusPackageInfo(coverages)}
                        </ul>
                    </div>
                    <div className="col-sm-6 align-right">
                        <div className="row text-center margin-top-13">
                            <Title tag="h3">
                                {`${numberWithSpaces(
                                    price
                                )} ${resolvePaymentTerm(paymentTerm, price)}`}
                            </Title>
                        </div>
                        {plusPackageBought ? (
                            <div className="row margin-top-39 flex-justify-center">
                                <SecondaryButton
                                    onClick={e => handlePlusPackageBuy(e)}>
                                    <Title
                                        tag={'h5'}
                                        children={getText(
                                            'webshopsweden.common.buyingInformation.remove'
                                        )}
                                    />
                                </SecondaryButton>
                            </div>
                        ) : (
                            <div className="row margin-top-39">
                                <PrimaryButton
                                    borderColor={'red'}
                                    flexible={true}
                                    onClick={e => handlePlusPackageBuy(e)}
                                    className={classNames(
                                        'position-absolute-left',
                                        isVillaagarna()
                                            ? 'villaagarna-green-bg-color no-border villaagarna-button'
                                            : ''
                                    )}>
                                    <Title
                                        tag={'h4'}
                                        children={getText(
                                            'webshopsweden.common.cardButton'
                                        )}
                                    />
                                </PrimaryButton>
                            </div>
                        )}
                    </div>
                    <div className="col-sm-1-5" />
                </div>
            )}
        </>
    );
};

export default PlusPackage;
